/**
 * Utility functions for updating EMR field values in subsection data
 * Single Responsibility: Field update operations
 * Follows DRY principle - reusable across components
 */

/**
 * Creates an EMR value object from raw field data
 * @param {*} value - The field value
 * @param {string} fieldType - Field type (TEXT, NUMBER, SELECT_ONE, etc.)
 * @param {string|null} displayValue - Display value for SELECT fields
 * @param {string|null} fieldLabel - Field label for the input field
 * @returns {Object} EMR value object with appropriate structure
 */
export function createEmrFieldValue(value, fieldType, displayValue = null, fieldLabel = null) {
  const emrValue = {}

  switch (fieldType) {
    case 'TEXT':
    case 'TEXTAREA':
      emrValue.valueString = value
      break
    case 'NUMBER':
      emrValue.valueNumber = value === '' ? null : Number(value)
      break
    case 'BOOLEAN':
      emrValue.valueBoolean = value
      break
    case 'DATE':
    case 'TIME':
    case 'DATETIME':
      emrValue.valueDate = value
      break
    case 'SELECT_ONE':
      emrValue.code = value
      emrValue.display = displayValue
      break
    case 'SELECT_MULTI':
      // For QUERY/HYBRID: value is array of EmrValueItem objects
      // For STATIC/VALUE_SET: value is array of codes
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        emrValue.valueStringList = value // Already EmrValueItem[]
      } else {
        emrValue.valueStringList = value // Array of codes
      }
      break
  }

  // Save field label as valueLabel (field input label)
  if (fieldLabel) {
    emrValue.valueLabel = fieldLabel
  }

  return emrValue
}

/**
 * Updates a single field in a subsection data object
 * @param {Object} subSectionData - The subsection data object
 * @param {string} fieldCode - The field code to update
 * @param {Object} emrValue - The EMR value object
 * @param {Object} subSection - The subsection definition (for code/label)
 * @returns {Object} Updated subsection data object
 */
export function updateFieldInSubSection(subSectionData, fieldCode, emrValue, subSection) {
  return {
    ...(subSectionData || {}),
    subSectionCode: subSection?.code,
    subSectionLabel: subSection?.title,
    values: {
      ...(subSectionData?.values || {}),
      [fieldCode]: emrValue,
    },
  }
}
